import { useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { canEdit } from "../lib/permissions";
import { Btn, Empty } from "../components/UI";
import { SlidePanel, ConfirmDialog, ReadOnlyChip } from "../components/SlidePanel";
import { FLabel, FInput, FSelect, FTextarea, CheckBox } from "./engagement/FormControls";
import { DateField } from "./engagement/DateField";
import { Plus, Pencil, Trash2, Phone, Mail, MapPin, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const RELATIONSHIPS = ["Mother", "Father", "Stepmother", "Stepfather", "Spouse", "Partner", "Sibling", "Child", "Grandparent", "Aunt/Uncle", "Legal Guardian", "Foster Parent", "Case Worker", "Other"];
const CONTACT_METHODS = ["Cell Phone", "Home Phone", "Email", "Text Message", "Mail"];
const GENDERS_F = ["Female", "Male", "Non-binary", "Prefer not to say"];
const FLAGS = [
  ["is_legal_guardian", "Legal Guardian"],
  ["has_custody", "Has Custody"],
  ["is_emergency_contact", "Emergency Contact"],
  ["lives_with_client", "Lives with Client"],
  ["involved_in_treatment", "Involved in Treatment"],
];
const EMPTY = { first_name: "", last_name: "", relationship: "", dob: "", gender: "", phone: "", alt_phone: "", email: "", address: "", preferred_contact: "Cell Phone", language: "English", employer: "", is_legal_guardian: false, has_custody: false, is_emergency_contact: false, lives_with_client: false, involved_in_treatment: false, roi_on_file: false, roi_expiration: "", notes: "", status: "active", emergency_priority: "" };
const PRIORITY_LABELS = { 1: "1st", 2: "2nd", 3: "3rd" };
const prLabel = (p) => PRIORITY_LABELS[Number(p)] || `${p}th`;

const FlagBadge = ({ label, tone = "brown" }) => (
  <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${tone === "green" ? "bg-[#E8F5E9] text-[#2E7D32]" : tone === "red" ? "bg-[#FFEBEE] text-[#C62828]" : "bg-[#EDE0CE] text-[#5D4037]"}`}>{label}</span>
);

export function FamilyPanel({ client, emergencyMode = false }) {
  const { user } = useAuth();
  const editable = canEdit(user, "demographics");
  const [items, setItems] = useState([]);
  const [panel, setPanel] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/clients/${client.id}/family`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [client.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const shown = emergencyMode
    ? items.filter((m) => m.is_emergency_contact).sort((a, b) => (Number(a.emergency_priority) || 99) - (Number(b.emergency_priority) || 99))
    : items;

  const f = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const openAdd = () => {
    const nextPriority = String(items.filter((m) => m.is_emergency_contact).length + 1);
    setForm({ ...EMPTY, is_emergency_contact: emergencyMode, emergency_priority: emergencyMode ? nextPriority : "" });
    setEditId(null); setPanel(true);
  };
  const openEdit = (m) => { setForm({ ...EMPTY, ...m }); setEditId(m.id); setPanel(true); };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editId) await api.put(`/family/${editId}`, form);
      else await api.post(`/clients/${client.id}/family`, form);
      toast.success(editId ? "Family member updated" : "Family member added");
      setPanel(false); load();
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/family/${confirmDelete.id}`);
      toast.success("Family member removed");
      setConfirmDelete(null); load();
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };

  return (
    <div data-testid={emergencyMode ? "emergency-panel" : "family-panel"}>
      <div className="flex items-center justify-between mb-4">
        {!editable ? <ReadOnlyChip /> : (
          emergencyMode ? <p className="text-xs text-[#7B6157]">Emergency contacts are managed in the client's family & support network - ordered by call priority.</p> : <span />
        )}
        {editable && <Btn data-testid={emergencyMode ? "add-emergency-btn" : "add-family-btn"} variant="outline" className="rounded-lg" onClick={openAdd}><Plus size={14} /> {emergencyMode ? "Add Emergency Contact" : "Add Family Member"}</Btn>}
      </div>
      {shown.length === 0 ? <Empty text={emergencyMode ? "No emergency contacts on file" : "No family members on file"} /> : (
        <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-4">
          {shown.map((m) => (
            <div key={m.id} data-testid={`family-card-${m.id}`} className={`rounded-2xl border p-4 ${m.status === "inactive" ? "bg-[#FAFAF7] border-[#EFEBE0] opacity-70" : "bg-[#F9F5EE] border-[#F1E8DA]"}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex items-start gap-2.5">
                  {emergencyMode && m.emergency_priority && (
                    <span data-testid={`priority-badge-${m.id}`} className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#C62828] text-xs font-bold text-white">{prLabel(m.emergency_priority)}</span>
                  )}
                  <div className="min-w-0">
                    <div className="font-heading font-bold text-[#3E2723] break-words">{m.first_name} {m.last_name}</div>
                    <div className="text-xs text-[#7B6157]">{m.relationship}{m.dob ? ` · DOB ${m.dob}` : ""}{m.status === "inactive" ? " · Inactive" : ""}</div>
                  </div>
                </div>
                {editable && (
                  <div className="flex gap-1.5 shrink-0">
                    <button type="button" data-testid={`edit-family-${m.id}`} aria-label="Edit" onClick={() => openEdit(m)}
                      className="h-8 w-8 rounded-full bg-white text-[#7B6157] flex items-center justify-center hover:bg-[#EDE7DC]"><Pencil size={13} /></button>
                    <button type="button" data-testid={`delete-family-${m.id}`} aria-label="Delete" onClick={() => setConfirmDelete(m)}
                      className="h-8 w-8 rounded-full bg-white text-[#7B6157] flex items-center justify-center hover:bg-[#FFEBEE] hover:text-[#C62828]"><Trash2 size={13} /></button>
                  </div>
                )}
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {m.is_legal_guardian && <FlagBadge label="Legal Guardian" tone="red" />}
                {m.has_custody && <FlagBadge label="Custody" tone="red" />}
                {m.is_emergency_contact && <FlagBadge label="Emergency Contact" />}
                {m.lives_with_client && <FlagBadge label="Lives with Client" />}
                {m.involved_in_treatment && <FlagBadge label="In Treatment" tone="green" />}
                {m.roi_on_file && <FlagBadge label={`ROI${m.roi_expiration ? ` exp ${m.roi_expiration}` : ""}`} tone="green" />}
              </div>
              <div className="mt-3 space-y-1 text-sm text-[#6D4C41]">
                {m.phone && <div className="flex items-center gap-1.5"><Phone size={12} className="text-[#84675B]" /> {m.phone}{m.preferred_contact === "Cell Phone" ? " (preferred)" : ""}</div>}
                {m.email && <div className="flex items-center gap-1.5 break-all"><Mail size={12} className="text-[#84675B]" /> {m.email}</div>}
                {m.address && <div className="flex items-center gap-1.5"><MapPin size={12} className="text-[#84675B]" /> {m.address}</div>}
              </div>
              {m.notes && <p className="mt-2 text-xs text-[#7B6157] bg-white/60 rounded-lg px-2.5 py-1.5">{m.notes}</p>}
            </div>
          ))}
        </div>
      )}

      <SlidePanel open={panel} onClose={() => setPanel(false)} title={editId ? (emergencyMode ? "Edit Emergency Contact" : "Edit Family Member") : (emergencyMode ? "Add Emergency Contact" : "Add Family Member")}
        subtitle={`${client.first_name} ${client.last_name} - family & support network`} testId="family-slide-panel">
        <form onSubmit={save} className="space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div><FLabel>First Name *</FLabel><FInput data-testid="family-first-name" value={form.first_name} onChange={f("first_name")} /></div>
            <div><FLabel>Last Name</FLabel><FInput data-testid="family-last-name" value={form.last_name} onChange={f("last_name")} /></div>
            <div><FLabel>Relationship *</FLabel>
              <FSelect data-testid="family-relationship" value={form.relationship} onChange={f("relationship")} placeholder="Select relationship">
                {RELATIONSHIPS.map((r) => <option key={r}>{r}</option>)}
              </FSelect></div>
            <div><FLabel>Date of Birth</FLabel><DateField testId="family-dob" value={form.dob} onChange={(v) => setForm((s) => ({ ...s, dob: v }))} /></div>
            <div><FLabel>Gender</FLabel><FSelect data-testid="family-gender" value={form.gender} onChange={f("gender")} placeholder="Select">{GENDERS_F.map((g) => <option key={g}>{g}</option>)}</FSelect></div>
            <div><FLabel>Preferred Language</FLabel><FInput value={form.language} onChange={f("language")} /></div>
            <div><FLabel>Cell Phone</FLabel><FInput data-testid="family-phone" value={form.phone} onChange={f("phone")} /></div>
            <div><FLabel>Alternate Phone</FLabel><FInput value={form.alt_phone} onChange={f("alt_phone")} /></div>
            <div><FLabel>Email</FLabel><FInput data-testid="family-email" type="email" value={form.email} onChange={f("email")} /></div>
            <div><FLabel>Preferred Contact</FLabel><FSelect value={form.preferred_contact} onChange={f("preferred_contact")}>{CONTACT_METHODS.map((c) => <option key={c}>{c}</option>)}</FSelect></div>
            <div className="col-span-2"><FLabel>Address</FLabel><FInput data-testid="family-address" value={form.address} onChange={f("address")} /></div>
            <div className="col-span-2"><FLabel>Employer / School</FLabel><FInput value={form.employer} onChange={f("employer")} /></div>
          </div>

          <div>
            <FLabel>Roles & Legal</FLabel>
            <div className="flex flex-wrap gap-2 mt-1">
              {FLAGS.map(([k, label]) => (
                <CheckBox key={k} label={label} checked={!!form[k]} onChange={() => setForm((s) => ({ ...s, [k]: !s[k] }))} testId={`family-flag-${k}`} />
              ))}
              <CheckBox label="ROI on File" checked={!!form.roi_on_file} onChange={() => setForm((s) => ({ ...s, roi_on_file: !s.roi_on_file }))} testId="family-flag-roi" />
            </div>
          </div>
          {form.is_emergency_contact && (
            <div className="w-56"><FLabel>Call Priority</FLabel>
              <FSelect data-testid="family-priority" value={form.emergency_priority} onChange={f("emergency_priority")} placeholder="Select priority">
                {["1", "2", "3", "4", "5"].map((p) => <option key={p} value={p}>{prLabel(p)} to call</option>)}
              </FSelect></div>
          )}
          {form.roi_on_file && (
            <div className="w-56"><FLabel>ROI Expiration</FLabel><DateField testId="family-roi-expiration" value={form.roi_expiration} onChange={(v) => setForm((s) => ({ ...s, roi_expiration: v }))} /></div>
          )}
          <div><FLabel>Status</FLabel>
            <FSelect data-testid="family-status" value={form.status} onChange={f("status")}>
              <option value="active">Active</option><option value="inactive">Inactive</option>
            </FSelect></div>
          <div><FLabel>Notes</FLabel><FTextarea data-testid="family-notes" rows={2} value={form.notes} onChange={f("notes")} /></div>

          <div className="flex justify-end gap-2 border-t border-[#F1EEE7] pt-4">
            <Btn type="button" variant="ghost" onClick={() => setPanel(false)}>Cancel</Btn>
            <Btn data-testid="family-save-btn" type="submit" disabled={busy || !form.first_name.trim() || !form.relationship}>
              {busy ? "Saving…" : editId ? "Save Changes" : "Add Member"}
            </Btn>
          </div>
        </form>
      </SlidePanel>

      <ConfirmDialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)} onConfirm={doDelete} busy={busy}
        title="Remove family member"
        description={`Remove ${confirmDelete?.first_name} ${confirmDelete?.last_name || ""} (${confirmDelete?.relationship}) from ${client.first_name}'s family record?`} />
    </div>
  );
}
